import React, { useCallback, useState, useEffect } from 'react';
import {
  Alert,
  Drawer,
  Box,
  Typography,
  IconButton,
  MenuItem,
  Stack,
  Button,
  Select,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import StyledInputField from '../../Components/Custom/StyledInputField';
import MuiTypographyMandatory from '../../Components/Custom/MuiTypographyMandatory';
import useCountryList from '../../Hooks/useCountryList';
import { align_column, VM_Colors } from '../../Styles/VMStyles';
import { isEmpty } from 'lodash';
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import {
  API_GET_ROLE_NAMES,
  API_RESEND_OTP,
  API_TEAM_REGISTRATION,
  API_UPDATE_USER_REGISTRATION
} from '../../Util/URLConstants';
import axios from 'axios';
import OTPDialog from '../../LoginRegistration/Registration/OTPDialog';
import Loader from '../../Components/Loader';
import StorageFactory from '../../Util/StorageFactory';
import { SESSION_USER_ID_KEY } from '../../Util/VMConstants';
import AddRolePermissions from './AddRolePermissions';
import { useSelector } from 'react-redux';
import {
  formatTrialTeamNotice,
  getSubscriptionGateFromResponse,
  isTrialSubscriptionPlan,
} from '../../Util/subscriptionLifecycleHelper';

const getDefaultCountry = (countryList = []) =>
  countryList.find((countryItem) => countryItem.cid === 101) ||
  countryList.find(
    (countryItem) => countryItem.country_name?.trim().toLowerCase() === "india"
  ) ||
  null;

const formRowSx = {
  width: "100%",
};

const selectFieldSx = {
  mt: 0.5,
  width: "100%",
  backgroundColor: VM_Colors.white,
  borderRadius: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    minHeight: 56,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C3C3C3",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#A0A0A0",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: VM_Colors.primary,
  },
};

const passwordFieldSx = {
  mt: 0.5,
  width: "100%",
  borderRadius: 1,
  backgroundColor: VM_Colors.white,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C3C3C3",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#A0A0A0",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: VM_Colors.primary,
  },
};

const primaryButtonSx = {
  mt: 2,
  py: 1.5,
  borderRadius: 2,
};

const AddTeamDrawer = ({ open, onClose, selectedUser, isEdit, onSubscriptionGate }) => {
  const { country } = useCountryList();
  const navigate = useNavigate();
  const teamLimit = useSelector((state) => state.subscriptionTier.teamLimit);
  const isTrialPlan = isTrialSubscriptionPlan(teamLimit);
  const trialNotice = isTrialPlan ? formatTrialTeamNotice(teamLimit) : '';

  const [form, setForm] = useState({
    name: '',
    email: '',
    designation: '',
    department: '',
    mobile_no: '',
    role: null,
    countryID: '',
    country_code: '',
    country_data: {},
    password: '',
    confirmPassword: '',
    form_user_id: '',
    existPhNo: '',
    existPhCode: ''
  });

  const [errors, setErrors] = useState({});
  const [roleOptions, setRoleOptions] = useState([]);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [showRoleAlert, setShowRoleAlert] = useState(false);

  const [state, setState] = useState({
    loading: false,
    showPassword: false,
    showConfirmPassword: false
  });

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(API_GET_ROLE_NAMES + "?user_id=" + userID);
      const resData = res.data;
      if (resData.status_code === 200 && Array.isArray(resData.data)) {
        setRoleOptions(resData.data);
      }
      if (selectedUser?.user_type && Array.isArray(resData.data)) {
        const selectedRoleObj = resData.data.find(
          (role) => role.role_name === selectedUser.user_type
        );
        setForm((prev) => ({
          ...prev,
          role: selectedRoleObj || null
        }));
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  }, [selectedUser?.user_type, userID]);

  useEffect(() => {
    if (open) {
      fetchRoles();
      setShowOtpDialog(false);
    }
  }, [open, fetchRoles]);

  useEffect(() => {
    if (!open || isEdit || !country.length) return;

    const defaultCountry = getDefaultCountry(country);
    if (!defaultCountry) return;

    setForm((prev) => {
      if (prev.countryID) return prev;

      return {
        ...prev,
        countryID: defaultCountry.cid,
        country_code: defaultCountry.country_code || "",
        country_data: defaultCountry,
      };
    });
  }, [open, isEdit, country]);

  const resetForm = useCallback(() => {
    const defaultCountry = getDefaultCountry(country);

    setForm({
      name: "",
      email: "",
      mobile_no: "",
      role: null,
      countryID: defaultCountry?.cid || "",
      country_code: defaultCountry?.country_code || "",
      country_data: defaultCountry || {},
      password: "",
      confirmPassword: "",
      form_user_id: "",
      existPhNo: "",
      existPhCode: "",
      designation: "",
      department: "",
    });
    setErrors({});
    setState((prev) => ({
      ...prev,
      showPassword: false,
      showConfirmPassword: false,
    }));
  }, [country]);

  const handleResendOTP = useCallback(async () => {
    if (!selectedUser?.user_id) return;
    setState((prev) => ({ ...prev, loading: true }));
    try {
      await axios.post(API_RESEND_OTP, { user_id: selectedUser.user_id });
      setShowOtpDialog(true);
    } catch (error) {
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [selectedUser?.user_id]);

  useEffect(() => {
    if (open && isEdit && selectedUser) {
      const selectedCid = selectedUser.country_data?.cid;
      const selectedRoleObj = roleOptions.find(
        (role) => role.role_name === selectedUser.user_type
      );

 setForm((prev) => ({
        ...prev,
        name: selectedUser.name || '',
        email: selectedUser.email || '',
        mobile_no: selectedUser.mobile_no || '',
        role: selectedRoleObj || null,
        countryID: selectedCid || selectedUser.countryID || '',
        country_code: selectedUser.country_code || '',
        country_data: selectedUser.country_data || {},
        password: '',
        confirmPassword: '',
        form_user_id: selectedUser.user_id,
        existPhNo: selectedUser.mobile_no,
        existPhCode: selectedUser.country_code,
        designation: selectedUser.designation || '',
        department: selectedUser.department || '',
      }));
      setErrors({});
    } else if (open && !isEdit) {
      resetForm();
    }
    if(!isEmpty(selectedUser) && selectedUser?.is_otp_verified === false){
      handleResendOTP();
    }
  }, [open, isEdit, selectedUser, roleOptions, handleResendOTP, resetForm]);



  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[name];

      if ((name === 'password' || name === 'confirmPassword') && form.confirmPassword && name === 'password') {
        if (value !== form.confirmPassword) updated.confirmPassword = 'Passwords do not match';
      }

      if (name === 'confirmPassword' && value !== form.password) {
        updated.confirmPassword = 'Passwords do not match';
      }

      return updated;
    });
  };

  const handleCountryChange = (e) => {
    const selectedCid = e.target.value;
    const selectedObj = country.find((c) => c.cid === selectedCid);
    setForm((prev) => ({
      ...prev,
      countryID: selectedCid,
      country_code: selectedObj?.country_code || '',
      country_data: selectedObj || {}
    }));

    setErrors((prev) => {
      const updated = { ...prev };
      delete updated.countryID;
      return updated;
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name) {
      newErrors.name = 'Name is required';
    }

    if (!form.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Invalid email';
    }

    // if (!form.designation) {
    //   newErrors.designation = 'Designation is required';
    // }

    // if (!form.department) {
    //   newErrors.department = 'Department is required';
    // }

    if (!form.countryID) {
      newErrors.countryID = 'Country is required';
    }

    if (!form.mobile_no) {
      newErrors.mobile_no = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(form.mobile_no)) {
      newErrors.mobile_no = 'Invalid mobile number';
    }

    if (!form.role || !form.role.id) {
      newErrors.role = 'Role is required';
    }

    if (!isEdit) {
      if (!form.password) newErrors.password = 'Password is required';
      if (!form.confirmPassword) {
        newErrors.confirmPassword = 'Confirm password is required';
      } else if (form.password !== form.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    let isMobileUpdate =
      form.country_code !== form.existPhCode || form.mobile_no !== form.existPhNo;

    setState({ ...state, loading: true });

    const reqBody = {
      user_id: isEdit ? form.form_user_id : undefined,
      name: form.name,
      mobile_no: form.mobile_no,
      email: form.email,
      designation: form.designation,
      department: form.department,
      country_code: form.country_code,
      is_otp_verified: false,
      firebase_token: "team_web",
      user_type: form?.role?.role_name,
      country_data: form.country_data,
      created_by: userID,
      mobileUpdate: isMobileUpdate,
      ...(isEdit ? {} : { password: form.password })
    };

    const apiEndpoint = isEdit ? API_UPDATE_USER_REGISTRATION : API_TEAM_REGISTRATION;

    axios.post(apiEndpoint, reqBody, { headers: { "Content-Type": "application/json" } })
      .then((response) => {
        setState({ ...state, loading: false });
        const respData = response.data;

        if (!isEdit) {
          const gate = getSubscriptionGateFromResponse(respData);
          if (gate && onSubscriptionGate) {
            onSubscriptionGate(gate);
            return;
          }
        }

        if (respData.status_code === 200) {
          setForm({ ...form, form_user_id: respData?.user_id });
          if (isMobileUpdate) {
            setShowOtpDialog(true);
          } else {
            setShowOtpDialog(false);
            onClose();
          }
        } else {
          if (respData?.isEmailAlreadyExist) {
            if (respData.is_otp_verified === false) {
              setShowOtpDialog(true);
            } else {
              alert("Email is already registered");
              setShowOtpDialog(false);
            }
          } else {
            alert(!isEmpty(respData?.message) ? respData.message : "Unexpected error");
          }
        }
      })
      .catch((error) => {
        setState({ ...state, loading: false });
        const gate = getSubscriptionGateFromResponse(error?.response?.data);
        if (!isEdit && gate && onSubscriptionGate) {
          onSubscriptionGate(gate);
          return;
        }
        console.warn("Error:", error.message);
      });
  };

  const handleVerify = () => {
    if (onClose) onClose();
  };

  const onReload = () => {
    fetchRoles();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 440 } },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${VM_Colors.light_gray}`,
          backgroundColor: VM_Colors.white,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {isEdit ? "Edit Member" : "Add Team Member"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isEdit
                ? "Update team member details and access role."
                : "Add a new member and assign their role."}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close drawer">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          p: 3,
          backgroundColor: VM_Colors.white2,
          minHeight: "100%",
          "& .MuiStack-root": {
            width: "100%",
            minWidth: 0,
          },
        }}
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          {!isEdit && isTrialPlan && trialNotice ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {trialNotice}
            </Alert>
          ) : null}
          <Box sx={formRowSx}>
            <StyledInputField
              heading="Name"
              name="name"
              required
              fullWidth
              value={form.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Box>

          <Box sx={formRowSx}>
            <StyledInputField
              heading="Email"
              name="email"
              required
              fullWidth
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Box>

          {/* <StyledInputField
            heading="Designation"
            name="designation"
            required
            fullWidth
            value={form.designation}
            onChange={handleChange}
            error={!!errors.designation}
            helperText={errors.designation}
          />

          <StyledInputField
            heading="Department"
            name="department"
            required
            fullWidth
            value={form.department}
            onChange={handleChange}
            error={!!errors.department}
            helperText={errors.department}
          /> */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "5fr 7fr" },
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <MuiTypographyMandatory label="Country" required />
              <Select
                name="countryID"
                value={form.countryID}
                onChange={handleCountryChange}
                fullWidth
                sx={selectFieldSx}
                error={!!errors.countryID}
              >
                {country.map((ctr) => (
                  <MenuItem key={ctr.cid} value={ctr.cid}>
                    {ctr.country_name}
                    {ctr.country_code ? ` (+${ctr.country_code})` : ""}
                  </MenuItem>
                ))}
              </Select>
              {errors.countryID && (
                <Typography variant="caption" color="error">
                  {errors.countryID}
                </Typography>
              )}
            </Box>

            <Box sx={{ width: "100%", minWidth: 0 }}>
              <StyledInputField
                heading={
                  form.country_code ? `Mobile No (+${form.country_code})` : "Mobile No"
                }
                name="mobile_no"
                required
                fullWidth
                type="number"
                value={form.mobile_no}
                onChange={handleChange}
                error={!!errors.mobile_no}
                helperText={errors.mobile_no}
                inputProps={{ maxLength: 10 }}
                placeholder="10-digit mobile number"
              />
            </Box>
          </Box>

          <Box sx={{ ...align_column, ...formRowSx }}>
            <MuiTypographyMandatory label="Role" required />

            <Select
              name="role"
              value={form.role?.id || ""}
              displayEmpty
              fullWidth
              sx={selectFieldSx}
              error={!!errors.role}
              onChange={(e) => {
                const selectedRole = roleOptions.find(
                  (r) => r.id === e.target.value
                );
                setForm((prev) => ({ ...prev, role: selectedRole || null }));
                setErrors((prev) => {
                  const updated = { ...prev };
                  delete updated.role;
                  return updated;
                });
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography color="text.secondary" fontSize={16}>
                      Select role
                    </Typography>
                  );
                }
                const selectedRole = roleOptions.find((role) => role.id === selected);
                return selectedRole?.role_name || "";
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select role
              </MenuItem>

              {roleOptions.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.role_name}
                </MenuItem>
              ))}

              <Divider />

              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/roles");
                }}
                sx={{
                  color: VM_Colors.primary,
                  fontWeight: 600,
                }}
              >
                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                Add New Role
              </MenuItem>
            </Select>

            {errors.role && (
              <Typography variant="caption" color="error">
                {errors.role}
              </Typography>
            )}
          </Box>

          {/* <Box sx={align_right}>
            <span
              style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                setShowRoleAlert(true);
              }}
            >
              Add Roles & Permissions
            </span>
          </Box> */}

          {!isEdit && (
            <>
              <Box sx={formRowSx}>
                <FormControl fullWidth variant="outlined">
                  <MuiTypographyMandatory label="Password" required />
                  <OutlinedInput
                    name="password"
                    placeholder="Enter your password"
                    type={state.showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    sx={passwordFieldSx}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setState((prev) => ({
                              ...prev,
                              showPassword: !prev.showPassword,
                            }))
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {state.showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {errors.password && (
                    <Typography variant="caption" color="error">
                      {errors.password}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={formRowSx}>
                <FormControl fullWidth variant="outlined">
                  <MuiTypographyMandatory label="Confirm Password" required />
                  <OutlinedInput
                    name="confirmPassword"
                    placeholder="Re-enter your password"
                    type={state.showConfirmPassword ? "text" : "password"}
                    value={form.confirmPassword}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    sx={passwordFieldSx}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setState((prev) => ({
                              ...prev,
                              showConfirmPassword: !prev.showConfirmPassword,
                            }))
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {state.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {errors.confirmPassword && (
                    <Typography variant="caption" color="error">
                      {errors.confirmPassword}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </>
          )}

          <Box sx={formRowSx}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              sx={primaryButtonSx}
              disabled={selectedUser?.is_otp_verified === false}
              fullWidth
            >
              {isEdit ? "Update Member" : "Add Member"}
            </Button>
          </Box>
        </Stack>
      </Box>

      <OTPDialog
        showOtpDialog={showOtpDialog}
        handleClose={() => setShowOtpDialog(false)}
        arrived="teamRegistration"
        user_id={form.form_user_id}
        handleOTPVerify={handleVerify}
      />

      {state.loading && <Loader loading={state.loading} />}

      {/* Alert Dialog */}
      <Dialog 
        open={showRoleAlert} 
        onClose={() => setShowRoleAlert(false)}
        maxWidth="lg" 
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        </DialogTitle>
        <DialogContent>
          <AddRolePermissions 
            onClose={() => setShowRoleAlert(false)} 
            editData={form.role}  
            onReload={onReload}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default AddTeamDrawer;
